/** biome-ignore-all lint/style/useNamingConvention: camcel case */
import cx from 'classnames';
import {useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useSelector, useStore} from 'react-redux';
import {SET_UPSELL_CONFIRMED} from '~/CreateFabricInvitation/reducers/modals';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {
  getEventId,
  selectEventStatus,
  selectTemplateName,
} from '~/common/fabric/reducers/eviteEvent/selectors';
import {UPDATE_GIFT_OPTIONS} from '~/common/fabric/reducers/giftingOptions/constants';
import {EVENT_STATUS_DRAFT} from '~/common/utils/event_constants';
import {trackUpsell} from '~/common/utils/upsellTracking';
import {showPremiumUpgradeSuccessToast} from '~/OneStepCreateInvitation/hooks/usePremiumFeatureBlocker';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {Accordion} from '~library/molecules/Accordion';
import {BottomSheet} from '~library/molecules/BottomSheet';
import {TrackingEvents} from '~library/shared/enums/TrackingEvents';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {DiamondIcon} from '~library/svg/DiamondIcon';
import {sendTrackingData} from '~library/utils/tracking';

import './UpsellModal.sass';
import './HostGalleryUpsellModal.sass';

const Step1Subtitle = () => {
  const isMediumAndUp = useIsMediumAndUp();
  return (
    <div>
      <Typography variant="paragraph2" color="pebble" className="upsell-modal__step1-text">
        You've selected a Premium feature — that's a nice touch.{isMediumAndUp ? <br /> : ' '}
        Switch to Premium now to keep those special customizations.
      </Typography>
    </div>
  );
};

export const UPSELL_MODES = Object.freeze({
  STEP_1_COPY: 'step-1-copy',
  REMOVE_ADS: 'remove-ads',
  UNLOCK_DESIGN_TOOLS: 'unlock-design-tools',
  GIFTING: 'gifting',
  SHAREABLE_LINK: 'shareable-link',
  CUSTOM_SHAREABLE_LINK: 'custom-shareable-link',
  CURATED_SET: 'curated-set',
  HOST_PHOTO_GALLERY: 'hostPhotoGallery',
});

const HEADER_TEXT = {
  [UPSELL_MODES.STEP_1_COPY]: {
    title: "You've got a real designer's eye!",
    subtitle: <Step1Subtitle />,
    // Experiment name: "test_halloween_pricing" cleanup ticket: EVT-35806 '*Premium Invitation packages start at $17.99.',
  },
  [UPSELL_MODES.REMOVE_ADS]: {
    title: 'Upgrade to remove ads!',
  },
  [UPSELL_MODES.UNLOCK_DESIGN_TOOLS]: {
    title: 'Make your invitation ✨pop✨',
  },
  [UPSELL_MODES.GIFTING]: {
    title: 'Upgrade to remove gift ideas',
  },
  [UPSELL_MODES.SHAREABLE_LINK]: {
    title: 'Create a custom URL',
  },
  [UPSELL_MODES.CUSTOM_SHAREABLE_LINK]: {
    title: 'Customize your shareable link',
  },
  [UPSELL_MODES.CURATED_SET]: {
    title: 'Save time with this curated set!',
  },
  [UPSELL_MODES.HOST_PHOTO_GALLERY]: {
    title: 'Curate your own host photo gallery',
    subtitle: 'Upload photos to a featured gallery on your Invitation.',
  },
};

const BANNER_IMG = {
  [UPSELL_MODES.HOST_PHOTO_GALLERY]: {
    url: 'https://g0.evitecdn.com/create-flow/designs/upsell-modal-features/host-photo-gallery-upsell-banner.png',
  },
};

const GET_WITH_PREMIUM_POINTS = {
  [UPSELL_MODES.STEP_1_COPY]: [],
  [UPSELL_MODES.REMOVE_ADS]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'Customize your shareable link',
  ],
  [UPSELL_MODES.UNLOCK_DESIGN_TOOLS]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'Customize your shareable link',
    'No ads!',
  ],
  [UPSELL_MODES.GIFTING]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'Customize your shareable link',
    'No ads!',
  ],
  [UPSELL_MODES.SHAREABLE_LINK]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'No ads!',
  ],
  [UPSELL_MODES.CUSTOM_SHAREABLE_LINK]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'No ads!',
  ],
  [UPSELL_MODES.CURATED_SET]: [
    'Exclusive designs, animated effects, and reveals',
    'Envelopes, stamps, and stickers',
    'Customize your shareable link',
    'No ads!',
  ],
  [UPSELL_MODES.HOST_PHOTO_GALLERY]: [
    'Backgrounds and animated effects',
    'Envelopes, stamps, and stickers',
    'Custom, shareable URL for your Invitation',
    'No ads!',
  ],
};

const isInvitationMaker = window.location.pathname.startsWith('/invitation-maker');

const ModalDetails = ({className = '', modalMode = UPSELL_MODES.REMOVE_ADS}) => {
  return (
    <div className={className}>
      {GET_WITH_PREMIUM_POINTS[modalMode].map((point) => (
        <div className="create-flow__upsell__feature" key={`feature_${point}`}>
          <DiamondIcon />
          <Typography variant="paragraph2">{point}</Typography>
        </div>
      ))}
      {/* Experiment name: "test_halloween_pricing" cleanup ticket: EVT-35806 */}
    </div>
  );
};

export const UpsellModalContent = ({
  containerDataQaId = 'upsell-modal',
  toggleUpsellModal,
  closeOverlay,
  noAutoRestore,
  onConfirmAction,
  cancelDataQaId,
  okDataQaId,
  closeDataQaId,
  upgradeToPremium,
  mode = 'gifting',
  hideCloseButton = false,
  hideSubtitle = false,
  className,
  cancelCtaText,
  useRedux = true,
  okCtaText,
  headerVariant = 'header4',
  inlineHeader = false,
  onDenyAction,
  bannerImg = '',
  accordionDetails = false,
  ref,
}) => {
  const templateName = useSelector(selectTemplateName);
  const eventId = useSelector(getEventId);
  const eventStatus = useSelector(selectEventStatus) || EVENT_STATUS_DRAFT;

  let store = null;
  if (useRedux) {
    store = useStore();
  }
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const [upsellIsSubmitting, setUpsellIsSubmitting] = useState(false);
  const isMediumAndUp = useIsMediumAndUp();
  const modalMode = mode in HEADER_TEXT ? mode : 'gifting';
  const isStep1 = modalMode === UPSELL_MODES.STEP_1_COPY;
  const accordionRef = useRef(null);
  const headerVariantAlt = isStep1 ? 'header5' : headerVariant;

  const onUpgradeToPremium = useCallback(() => {
    trackUpsell({
      path: isInvitationMaker
        ? '/invitation-maker'
        : window.location.pathname.replace(/\/$/, '').split('/').at(-1),
      feature: mode,
      status: eventStatus,
    });
    sendTrackingData(TrackingEvents.CfUpsellTracking, {
      identifier: `upsell_modal_${mode}`,
      templateId: templateName,
      eventId: eventId,
      status: eventStatus,
    });
    if (useRedux) {
      store.dispatch({
        type: SET_UPSELL_CONFIRMED,
        payload: true,
      });
      store.dispatch({
        type: UPDATE_EVENT_DETAILS,
        payload: {isPremium: true},
      });
    }
    showPremiumUpgradeSuccessToast();
    upgradeToPremium?.();
  }, [mode, store, useRedux, upgradeToPremium, templateName, eventStatus, eventId]);

  const premiumBadge = isStep1 ? (
    <div className="upsell-modal__badge--step-1-copy">
      <DiamondIcon ratio={1.14} />
      <Typography variant="list1" color="premiumColor" inline>
        Premium
      </Typography>
    </div>
  ) : (
    <div>
      <img
        src="https://g0.evitecdn.com/marketing/assets/PremiumDiamondAni.gif"
        alt=""
        width={142}
        height={108}
        className="create-flow__upsell__diamond-ani"
      />
      <Typography variant={headerVariantAlt} color="premiumColor" inline>
        Premium
      </Typography>
    </div>
  );

  return (
    <div
      className={cx('create-flow__upsell-modal-content', `upsell-modal__${modalMode}`, className)}
      ref={ref}
      data-qa-id={containerDataQaId}
    >
      {hideCloseButton ? null : (
        <button
          className="create-flow__upsell-modal-close-btn"
          data-qa-id={closeDataQaId || 'upsell-close'}
          onClick={closeOverlay ?? toggleUpsellModal}
          type="button"
          aria-label="Close modal"
          disabled={upsellIsSubmitting}
          aria-disabled={upsellIsSubmitting}
        >
          <CloseLineIcon ratio={0.75} />
        </button>
      )}
      <div className="create-flow__upsell__content">
        <div className={cx('create-flow__upsell__header', {inline: inlineHeader})}>
          {premiumBadge}
          <Typography className="create-flow__upsell__title" variant={headerVariantAlt}>
            {HEADER_TEXT[modalMode].title}
          </Typography>
          {HEADER_TEXT[modalMode].subtitle && !hideSubtitle ? (
            <Typography variant="paragraph2">{HEADER_TEXT[modalMode].subtitle}</Typography>
          ) : null}
        </div>
        {bannerImg && (
          <img
            className="create-flow_upsell__banner-img"
            src={BANNER_IMG[modalMode].url}
            alt="host gallery example banner"
          />
        )}
        {accordionDetails ? (
          <Accordion
            accordionBodyWrapperId="upsell-modal-accordion-details"
            label="Explore all Premium features"
            labelVariant="paragraph2"
            chevron={<ChevronIcon direction={accordionExpanded ? 'up' : 'down'} ratio={0.75} />}
            onClose={() => setAccordionExpanded(false)}
            onOpen={() => setAccordionExpanded(true)}
            arrowPlacement="right"
            isOpen={accordionExpanded}
            ref={accordionRef}
            accordionQaId="upsell-modal-accordion-details-toggle"
          >
            <ModalDetails
              className="create-flow_upsell__feature-list-accordion"
              modalMode={modalMode}
            />
          </Accordion>
        ) : (
          <ModalDetails className="create-flow_upsell__feature-list" modalMode={modalMode} />
        )}
      </div>
      <div className={cx({'create-flow__upsell__ctas': true})}>
        <Button
          variant="primary"
          data-qa-id={okDataQaId || 'upsell-upgrade-now'}
          onClick={() => {
            setUpsellIsSubmitting(true);

            onUpgradeToPremium();
            onConfirmAction();

            toggleUpsellModal(false);
          }}
          size="large"
          noTextWrap
          fullWidth
          disabled={upsellIsSubmitting}
        >
          {okCtaText || 'Switch to Premium'}
        </Button>
        <Button
          variant={isMediumAndUp ? 'secondary' : 'unstyled-button'}
          data-qa-id={cancelDataQaId || 'upsell-back'}
          className="upsell-stay-in-basic"
          onClick={() => {
            if (onDenyAction) {
              onDenyAction();
              return closeOverlay();
            }

            if (!noAutoRestore && store) {
              store.dispatch({
                type: UPDATE_GIFT_OPTIONS,
                payload: {
                  options: {event_option_show_gifting: true},
                  showGifting: true,
                },
              });
            }
            return closeOverlay();
          }}
          size="large"
          noTextWrap
          fullWidth
          disabled={upsellIsSubmitting}
        >
          {(isStep1 ? 'Keep it free' : cancelCtaText) || 'Stay in basic'}
        </Button>
      </div>
    </div>
  );
};

export const UpsellModal = (props) => {
  const {toggleUpsellModal, aboveOtherModals, ref} = props;
  const isMediumAndUp = useIsMediumAndUp();
  const upsellModalRef = useRef(null);

  const templateName = useSelector(selectTemplateName);
  const eventId = useSelector(getEventId);
  const eventStatus = useSelector(selectEventStatus);

  useEffect(() => {
    sendTrackingData(TrackingEvents.ContentLoadTracking, {
      identifier: `upsell_modal_${props.mode}`,
      attributes: {
        templateId: templateName,
        eventId: eventId,
        status: eventStatus,
      },
    });
  }, [templateName, eventId, eventStatus, props.mode]);

  useEffect(() => {
    if (!upsellModalRef.current) return;

    upsellModalRef.current.style.height = `${upsellModalRef.current.offsetHeight}px`;
  }, []);

  // TODO Refactor to use ResponsiveModal
  return createPortal(
    <div
      className={cx('create-flow__upsell-modal', {
        'create-flow__upsell-modal--above-others': aboveOtherModals,
      })}
    >
      {/* TODO: Refactor to PB Button component */}
      <button
        className="unstyled-button create-flow__upsell-modal-overlay"
        onClick={toggleUpsellModal}
        type="button"
        aria-label="Upsell modal backdrop"
      />
      {isMediumAndUp ? (
        <UpsellModalContent {...props} ref={ref} />
      ) : (
        <BottomSheet
          content={<UpsellModalContent {...props} ref={ref} />}
          onClose={() => toggleUpsellModal(false)}
          aboveOtherModals
          in
        />
      )}
    </div>,
    document.body
  );
};
